/**
 * Action sentiment, defined once.
 *
 * Detail-page action bars were flat walls of identical `variant="outline"`
 * buttons — "Change Stage", "Mark Won", "Mark Lost", "Send Email", "Create
 * Quote" all reading with equal weight, so nothing pointed at the action a rep
 * usually wants and nothing warned about the one they rarely do. Where colour
 * WAS applied it was retyped inline per button, so green/red drifted between
 * screens.
 *
 * An action declares its INTENT; the styling for that intent lives here.
 */

/** What an action means, independent of how it looks. */
export type ActionIntent =
  /** The action a user most often wants here. Exactly one per bar. */
  | "primary"
  /** Advances/completes the record favorably (Mark Won, Approve, Publish). */
  | "positive"
  /** Closes it out unfavorably or discards (Mark Lost, Reject, Void). */
  | "negative"
  /** Irreversible removal (Delete). Reserved — always confirm first. */
  | "destructive"
  /** Everything else: ordinary, side-effect-light actions. */
  | "neutral";

/** The shadcn Button variant each intent maps to. */
export const INTENT_BUTTON_VARIANT: Record<
  ActionIntent,
  "default" | "outline" | "destructive"
> = {
  primary: "default",
  positive: "outline",
  negative: "outline",
  destructive: "destructive",
  neutral: "outline",
};

/**
 * Additional classes per intent. Positive/negative stay OUTLINE buttons with a
 * tinted border and text: they carry sentiment without competing with the
 * primary action for attention, and both light and dark themes are covered
 * (the inline versions of these classes frequently forgot dark mode).
 */
/**
 * Action-id fragments that imply an intent, for action catalogs that predate
 * the explicit `intent` field. Checked in order — the first match wins, so
 * destructive beats positive for something like `approve-and-delete`.
 *
 * An explicit `intent` on the action always overrides this guess.
 */
const INTENT_BY_ID_FRAGMENT: ReadonlyArray<{ intent: ActionIntent; fragments: readonly string[] }> = [
  { intent: "destructive", fragments: ["delete", "remove", "archive", "void"] },
  { intent: "negative", fragments: ["lost", "reject", "decline", "cancel"] },
  { intent: "positive", fragments: ["complete", "approve", "activate", "won", "publish"] },
];

/**
 * Resolve an action's intent: the explicit value if it has one, otherwise a
 * guess from its id, otherwise neutral.
 *
 * This is the single place sentiment is decided. It replaced a separate
 * `getActionVariant` heuristic that mapped ids to raw button variants — two
 * systems disagreeing about what "approve" should look like.
 */
export function resolveActionIntent(
  actionId: string,
  explicitIntent?: ActionIntent
): ActionIntent {
  if (explicitIntent) return explicitIntent;

  const normalized = actionId.toLowerCase();
  for (const { intent, fragments } of INTENT_BY_ID_FRAGMENT) {
    if (fragments.some((fragment) => normalized.includes(fragment))) return intent;
  }
  return "neutral";
}

export const INTENT_BUTTON_CLASSES: Record<ActionIntent, string> = {
  primary: "",
  positive:
    "text-green-700 border-green-200 hover:bg-green-50 hover:text-green-800 dark:text-green-300 dark:border-green-800 dark:hover:bg-green-950/40",
  negative:
    "text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40",
  destructive: "",
  neutral: "",
};
